import { HashRouter, Route, Routes, Navigate } from "react-router-dom"
import { useIsAuthenticated } from "@azure/msal-react"
import Dashboard from "./pages/protected/Dashboard.jsx"
import Landing from "pages/public/Landing.jsx"
import { Toast, ToastContainer, Spinner } from "react-bootstrap"
import { useState, useEffect } from "react"
import apiFetch from "apiFetch"
import { AuthContextProvider } from "store/auth-context.js"
import * as Yup from 'yup'

Yup.addMethod(Yup.string, "csvInputCheck", function (errorMessage) {
    return this.test(`csv-input-check`, errorMessage, function (value) {
        const { path, createError } = this;
        return (
            value === undefined ||
            value === null ||
            value === "" ||
            (value.indexOf(',') === -1 && value.indexOf('"') === -1) ||
            createError({ path, message: errorMessage })
        );
    });
});

const appendMessage = (messages, value) => {
    if (typeof value === 'string' && value.trim()) {
        messages.push(value.trim())
    }
}

const getObjectErrorMessages = errorData => {
    if (!errorData || typeof errorData !== 'object') {
        return []
    }

    let messages = []

    appendMessage(messages, errorData.message)
    appendMessage(messages, errorData.detail)
    appendMessage(messages, errorData.title)

    let keys = Object.keys(errorData)

    keys.forEach(function (key) {
        let value = errorData[key]

        if (Array.isArray(value)) {
            messages.push(...value.filter(Boolean))
        } else if (value && typeof value === 'object') {
            messages.push(...getObjectErrorMessages(value))

            if (Array.isArray(value.errors)) {
                messages.push(...value.errors.filter(Boolean))
            }
        }
    });

    return [...new Set(messages)]
}

const getNetworkErrorMessages = error => {
    if (error.config?.url && error.message) {
        return [`Network Error: ${error.message}: ${error.config.url}`]
    }

    if (error.config?.url) {
        return [`Network Error: cannot reach ${error.config.url}`]
    }

    if (error.message) {
        return [error.message]
    }

    return ["Unknown error"]
}

const getErrorMessages = error => {
    if (!error) {
        return ["Unknown error"]
    }

    const response = error.response
    const responseData = response?.data

    if (!response) {
        return getNetworkErrorMessages(error)
    }

    if (responseData?.title && responseData?.errors) {
        let structuredMessages = [responseData.title]
        let fields = Object.keys(responseData.errors)

        fields.forEach(function (key) {
            let fieldErrors = responseData.errors[key]
            structuredMessages.push(...fieldErrors)
        });

        return structuredMessages
    }

    if (typeof responseData === 'string' && responseData.trim()) {
        return [responseData]
    }

    const objectMessages = getObjectErrorMessages(responseData)
    if (objectMessages.length > 0) {
        return objectMessages
    }

    if (response.status) {
        return [`${response.status} ${response.statusText}`.trim()]
    }

    if (error.message) {
        return [error.message]
    }

    return ["Unknown error"]
}

const App = () => {
    const [loading, setLoading] = useState(false)
    const [showToastMsg, setShowToastMsg] = useState({ visible: false, msg: '' })

    useEffect(() => {
        // Start the spinner on any request.
        const reqId = apiFetch.interceptors.request.use(cfg => {
            setLoading(true)
            return cfg
        })

        // Global error handling for API requests.
        const resId = apiFetch.interceptors.response.use(function (response) {
            // Any status code that lie within the range of 2xx cause this function to trigger.
            setLoading(false)
            return response
        }, function (error) {
            // Any status codes that falls outside the range of 2xx cause this function to trigger.
            setLoading(false)
            const errorMessages = getErrorMessages(error)

            setShowToastMsg({ visible: true, msg: errorMessages.join("\n").trim() })
            return Promise.reject(error);
        })

        return () => {
            apiFetch.interceptors.request.eject(reqId)
            apiFetch.interceptors.response.eject(resId)
        }
    }, [])

    return (
        <>
            {/* Request spinner overlay */}
            {loading && (
                <output className="spinner-overlay">
                    <Spinner animation="border" aria-hidden="true" />
                    <span className="visually-hidden">Loading…</span>
                </output>
            )}
            <HashRouter>
                <Routes>
                    <Route index element={<LandingNav />} />
                    <Route
                        path="dashboard/*"
                        element={
                            <ProtectedRoute isAuthenticated={useIsAuthenticated()}>
                                <AuthContextProvider>
                                    <Dashboard />
                                </AuthContextProvider>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<p>There's nothing here: 404!</p>} />
                </Routes>
            </HashRouter>

            <ToastContainer className="p-3" position="bottom-center">
                <Toast
                    className="d-inline-block m-1"
                    bg="danger"
                    onClose={() => setShowToastMsg({ visible: false, msg: '' })}
                    show={showToastMsg.visible}
                >
                    <Toast.Header>
                        <strong className="me-auto">Error</strong>
                    </Toast.Header>
                    <Toast.Body className="Danger text-white" style={{ whiteSpace: 'pre-line' }}>
                        {showToastMsg.msg}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    )
}

const ProtectedRoute = ({ isAuthenticated, children }) => {
    return isAuthenticated ? children : <Navigate to="/" replace />
}

const LandingNav = () => {
    return useIsAuthenticated() ? <Navigate to="/dashboard" replace /> : <Landing />
}

export default App